/**
 * Play landing — chooser between Solo practice and 1-on-1 challenge.
 *
 * Kept as a small intermediate page so old links (home tiles, profile,
 * etc.) that point at `/play` still land somewhere meaningful. Tap either
 * card to jump to the focused page for that mode.
 *
 * Also hosts `renderInviteJoin` — the `/play/invite/{code}` handler that
 * an unauthed visitor to a shared invite URL hits. Unchanged from the
 * earlier monolithic play page.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { navigate } from '../router.js';

export async function renderPlay(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Play</div>
    </div>
    <div class="pad">
      <a href="/play/solo" data-link class="card" style="display:block;padding:18px;text-decoration:none;color:inherit;border:1px solid var(--violet);background:var(--surf-2);margin-bottom:14px;">
        <div class="row" style="align-items:center;">
          <div style="font-size:34px;line-height:1;margin-right:14px;">🧠</div>
          <div class="grow">
            <div style="font-family:var(--font-display);font-size:16px;font-weight:600;color:#fff;">
              Solo practice
            </div>
            <div class="muted" style="font-size:12px;margin-top:2px;">
              Free · 10 questions · learn at your own pace
            </div>
          </div>
          <div style="color:var(--mute);font-size:22px;">›</div>
        </div>
      </a>

      <a href="/play/duel" data-link class="card" style="display:block;padding:18px;text-decoration:none;color:inherit;border:1px solid #E94BA2;background:var(--surf-2);margin-bottom:14px;">
        <div class="row" style="align-items:center;">
          <div style="font-size:34px;line-height:1;margin-right:14px;">⚡</div>
          <div class="grow">
            <div style="font-family:var(--font-display);font-size:16px;font-weight:600;color:#fff;">
              1-on-1 challenge
            </div>
            <div class="muted" style="font-size:12px;margin-top:2px;">
              Free or staked · race a real player · best speed + accuracy wins
            </div>
          </div>
          <div style="color:var(--mute);font-size:22px;">›</div>
        </div>
      </a>

      <a href="/live" data-link class="card" style="display:block;padding:14px 18px;text-decoration:none;color:inherit;">
        <div class="row" style="align-items:center;">
          <div style="font-size:24px;line-height:1;margin-right:14px;">🎙️</div>
          <div class="grow">
            <div style="font-family:var(--font-display);font-size:14px;font-weight:600;color:#fff;">
              Live trivia
            </div>
            <div class="muted" style="font-size:11px;margin-top:1px;">
              Scheduled rooms with prize pools
            </div>
          </div>
          <div style="color:var(--mute);font-size:18px;">›</div>
        </div>
      </a>
    </div>
  `;
}

/**
 * Companion route: /play/invite/:code — instant-join from a shared link.
 *
 * Auth handling:
 *   - Signed in     → POST /match/invite/join → /match/:id
 *   - Not signed in → stash the code, bounce to /signup. New players (the
 *     common case for an invite link shared on WhatsApp/Twitter) get the
 *     shortest path to being in the room. signup.js + login.js both
 *     consume `triviana.pending_invite` on success.
 */
export async function renderInviteJoin(root) {
  const code = window.location.pathname.split('/').pop();
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Joining…</div>
    </div>
    <div class="pad">
      <div class="card">
        <div class="muted">Looking up invite ${escape(code)}…</div>
      </div>
    </div>
  `;
  try {
    const match = await api('/match/invite/join', { method: 'POST', body: { code } });
    navigate(`/match/${match.id}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      sessionStorage.setItem('triviana.pending_invite', code);
      navigate('/signup');
      return;
    }
    root.querySelector('.pad').innerHTML = `
      <div class="card" style="color:var(--pink-t);">
        ${escape(err.message || 'Could not join.')}
      </div>
      <a href="/play/duel" data-link class="btn ghost">Back to matchmaking</a>
    `;
  }
}
